import { Button, Popover, InputNumber, Modal, Typography, Select } from "antd";
import { audioMessage as message } from "../Common/Notifications/audioMessage";
import React from "react";
import { useState, useEffect } from "react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import "./styles.css";
import { TrainingAPI } from "../../api/TrainingAPI";
import { PiKeyFill, PiKeyBold, PiKey } from "react-icons/pi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useSelector, useDispatch } from "react-redux";
import { getColorStrategies } from "../../store/actions/team.actions";
import { 
  PermissionedButton, 
  PermissionedInputNumber, 
  PermissionedSelect 
} from "../PermissionedComponents";
const trainingAPI = new TrainingAPI();
const { Option } = Select;
const { Text } = Typography;

export const MetricsSettings = ({allMetrics, getAllMetrics}) => {
  const [showKeyIcon, setShowKeyIcon] = useState({}); // State to control icon visibility
  const [keyMetricSelection, setKeyMetricSelection] = useState([]);
  const [itemList, setItemList] = useState(allMetrics);
  const [isModalVisible, setIsModalVisible] = useState(false);
  
  const dispatch = useDispatch();
  
  const {
    info: { custom_colors_enabled },
    misc: { user_permissions: userPermissions },
    color_strategies: colorStrategies
  } = useSelector(({ team }) => team);

  // Check if user has permission to edit metrics
  const hasEditMetricsPermission = userPermissions?.EditMetrics;

  useEffect(() => {
    setItemList(allMetrics);

    let key_metrics = [], show_key_icon = {};
    allMetrics.map((metric, index) => {
      if (metric.is_key_metric === true) {
        key_metrics.push(metric.key); 
        show_key_icon[index] = true;
      } else {
        show_key_icon[index] = false;
      }
    })
    setKeyMetricSelection(key_metrics);
    setShowKeyIcon({...showKeyIcon, ...show_key_icon});
    
    // Fetch color strategies
    dispatch(getColorStrategies());
  }, [allMetrics, dispatch]);

  // Function to update list on drop
  const handleDrop = (droppedItem) => {
    // Check permission before allowing drag and drop
    if (!hasEditMetricsPermission) {
      return;
    }
    
    // Ignore drop outside droppable container
    if (!droppedItem.destination) return;
    var updatedList = [...itemList];
    // Remove dragged item
    const [reorderedItem] = updatedList.splice(droppedItem.source.index, 1);
    // Add dropped item
    updatedList.splice(droppedItem.destination.index, 0, reorderedItem);
    // Update State
    setItemList(updatedList);
  };

  const handleSave = () => {
    setIsModalVisible(false);

    trainingAPI
      .saveMetricOrdering(
        itemList.reduce(
          (cur, item, index) => ({ 
            ...cur, 
            [item.metric_id]: {
              index: index + 1,
              decimal_places: item.decimal_places, 
              weekly_bucket_multiple: item.weekly_bucket_multiple,
              is_key_metric: keyMetricSelection.includes(item.key),
              visible: item.visible,
              color_strategy_id: item.color_strategy_id
            }, 
          }),
          {}
        )
      )
      .then((data) => {
        message.success("Metrics updated successfully");
        getAllMetrics();
      })
      .catch((err) => message.error("Error saving metric order"));
  };

  const handleMetricClick = (metric_key, metric_index, checked) => {
    // Check permission before allowing key metric selection
    if (!hasEditMetricsPermission) {
      return;
    }
    
    setShowKeyIcon({...showKeyIcon, [metric_index]: checked});
    if (checked) {
      setKeyMetricSelection([...keyMetricSelection, metric_key]);
      setItemList(itemList.map((metric) => {
        if (metric.key === metric_key) {
          return {...metric, visible: true}
        }
        return metric
      }))
    } else {
      setKeyMetricSelection(keyMetricSelection.filter(key => key !== metric_key));
    }
  }

  const handleVisibleClick = (item, currVisibility) => {
    // Check permission before allowing visibility changes
    if (!hasEditMetricsPermission) {
      return;
    }
    
    const changeVisibilityTo = !currVisibility

    const metric = itemList.find((e) => e.metric_id === item.metric_id)
    const metricKeyRealTime = showKeyIcon[metric.order - 1]

    if (metricKeyRealTime && changeVisibilityTo === false) {
      message.warning("Key metrics cannot be hidden");
      return
    }

    
    setItemList(itemList.map((metric) => {
      if (metric.metric_id === item.metric_id) {
        return {...metric, visible: changeVisibilityTo};
      }
      return metric;
    }))
  }
  
  const handleColorStrategyChange = (metric_id, strategy_id) => {
    // Check permission before allowing color strategy changes
    if (!hasEditMetricsPermission) {
      return;
    }
    
    setItemList(itemList.map((metric) => {
      if (metric.metric_id === metric_id) {
        return {...metric, color_strategy_id: strategy_id};
      }
      return metric;
    }));
  };

  const showModal = () => {
    if (keyMetricSelection.length !== 4) {
      message.error("Please select relevant 4 key metrics.")
      return
    }

    //Check if key metrics are changed
    let oldKeyMetrics = [];
    itemList.map((metric) => {
      if (metric.is_key_metric) {
        oldKeyMetrics.push(metric.key);
      }
    })

    if (keyMetricSelection.length !== oldKeyMetrics.length) return false;  
    const keyMetricChanged = keyMetricSelection.every(item => oldKeyMetrics.includes(item));
    if (!keyMetricChanged) {
      setIsModalVisible(true);
    } else {
      handleSave()
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  return (
    <>
      <ul className="metric-setting-info">
        <li>
          <Text type="secondary">Metric Ordering: Drag and drop the metrics to change the order in which they appear on the platform.</Text>
        </li>
        <li>
          <Text type="secondary">Decimal Places: Number of decimal places upto which the values will be rounded upto on platform and reports</Text>
        </li>
        <li>
          <Text type="secondary">Weekly Bucket Multiplier: This multiplier is used to calculate weekly bucket benchmarks on the overview tab.</Text>
        </li>
        <li>
          <Text type="secondary">
            Selecting key metrics: You can select 4 key metrics. These are the most important metrics you'd like to see with extra analysis on the platform and reports. (Only cumulative metrics can be key metrics)
          </Text>
        </li>
        <li>
          <Text type="secondary">
            Color Strategy: Select a color strategy to apply to this metric. Color strategies determine how values are displayed.
          </Text>
        </li>
      </ul>
      <br></br>
      <div style={{ display: "flex" }}>
        <div style={{ width: 1150, maxWidth: "100%" }}>
          <div
            style={{
              marginBottom: 8,
              display: "flex",
              alignItems: "center",
              gap: 16,
            }}
          >
          <Modal title="Are you sure?" open={isModalVisible} onOk={handleSave} onCancel={handleCancel} width={"75vw"}>
            <div className="confirm-metric-settings-modal">
              <Typography.Title level={5} type="danger">CAUTION! You are about to change your key metrics</Typography.Title>
              <p>
                Changing your key metrics can have unexpected consequences to your analysis and reports.
                Please read the following information to understand how this affects your account.
              </p>
              <ol>
                <li>
                  <strong>Intensity</strong>: Your 4 key metrics are used to calculate the score and intensity of a practice on a given day. Changing them will update all the values (historical values included) on the calendar.
                </li>
                <li>
                  <strong>ACWR and Readiness Table</strong>: The 4 metrics that appear on the table on the Overview Tab and the readiness derived from them will be updated to use the new key metrics.
                </li>
                <li>
                  <strong>GPS Report</strong>: Your key metrics may have some extra analysis in your GPS Reports, either in the form of charts or tiles. Changing the 4 key metrics will be reflected on the report.
                </li>
                <li>
                  <strong>Overworked/Underworked Flags</strong>: Your key metrics are used to determine if a player is flagged for being underworked or overworked. Changing your key metrics may affect the players you see being flagged in your GPS Reports.
                </li>
              </ol>
            </div>
          </Modal>
          <PermissionedButton
            type="primary"
            onClick={showModal}
            permissionCheck="EditMetrics"
          >
            Save Changes
          </PermissionedButton>
          </div>
          <DragDropContext onDragEnd={handleDrop}>
            <Droppable droppableId="list-container">
              {(provided) => {
                return (
                  <div
                    className="list-container"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {itemList.map((item, index) => (
                      <Draggable
                        key={item.metric_id}
                        draggableId={`${item.metric_id}`}
                        index={index}
                        isDragDisabled={!hasEditMetricsPermission}
                      >
                        {(provided) => (
                          <div
                            className={keyMetricSelection?.includes(item.key) ? "item-container key-metric" : "item-container"}
                            ref={provided.innerRef}
                            {...provided.dragHandleProps}
                            {...provided.draggableProps}
                          >
                          {item.is_key_metric && (
                            <div style={{marginLeft: '5px'}} onClick={() => handleMetricClick(item.key, index, !showKeyIcon[index])}>
                              {keyMetricSelection?.includes(item.key) ? 
                                <PiKeyFill style={{color: '#ffc000', cursor: hasEditMetricsPermission ? "pointer" : "not-allowed", opacity: hasEditMetricsPermission ? 1 : 0.5}} /> : 
                                <PiKeyBold style={{ cursor: hasEditMetricsPermission ? "pointer" : "not-allowed", opacity: hasEditMetricsPermission ? 1 : 0.5}}/>
                              }
                            </div>
                          )}
                          {!item.is_key_metric && item.ACWR && (
                            <div style={{marginLeft: '5px'}} onClick={() => handleMetricClick(item.key, index, !showKeyIcon[index])}>
                              {keyMetricSelection?.includes(item.key) ? 
                                <PiKeyFill style={{color: '#ffc000', cursor: hasEditMetricsPermission ? "pointer" : "not-allowed", opacity: hasEditMetricsPermission ? 1 : 0.5}}/> : 
                                <PiKeyBold style={{ cursor: hasEditMetricsPermission ? "pointer" : "not-allowed", opacity: hasEditMetricsPermission ? 1 : 0.5}}/>
                              }
                            </div>
                          )}
                          {!item.is_key_metric && !item.ACWR && (
                            <div style={{marginLeft: '5px'}} >
                              <PiKey style={{ color: '#888888', cursor: "not-allowed"}}/>
                            </div>
                          )}

                            <strong className="metric-item-element">
                            {item.name}
                            </strong>
                            <div
                              className="decimal-input"
                            >
                              <PermissionedInputNumber
                                addonBefore="Decimal Places"
                                min={0}
                                max={3} 
                                value={item.decimal_places}
                                size="small" 
                                style={{width: '170px'}}
                                permissionCheck="EditMetrics"
                                onChange={(value) => {
                                  setItemList(itemList.map((metric) => {
                                    if (metric.metric_id === item.metric_id) {
                                      return {...metric, decimal_places: value};
                                    }
                                    return metric;
                                  }))
                                }}
                                />
                            </div>
                            <div
                                className="weekly-multiplier-input"
                                style={{width: '240px'}}
                              >
                              { keyMetricSelection?.includes(item.key) && (
                                <PermissionedInputNumber
                                  addonBefore="Weekly Bucket Multiplier"
                                  value={item.weekly_bucket_multiple}
                                  size="small" 
                                  style={{width: '240px'}}
                                  permissionCheck="EditMetrics"
                                  onChange={(value) => {
                                    setItemList(itemList.map((metric) => {
                                      if (metric.metric_id === item.metric_id) {
                                        return {...metric, weekly_bucket_multiple: value};
                                      }
                                      return metric;
                                    }))
                                  }}
                                />
                              )}
                            </div>
                            {custom_colors_enabled && (
                              <div
                                className="color-strategy-input"
                                style={{width: '350px'}}
                              >
                                <PermissionedSelect
                                  placeholder="Select color strategy"
                                  value={item.color_strategy_id}
                                  style={{width: '350px'}}
                                  size="small"
                                  permissionCheck="EditMetrics"
                                  onChange={(value) => handleColorStrategyChange(item.metric_id, value)}
                                >
                                  {colorStrategies.map((strategy) => (
                                    <Option key={strategy.id} value={strategy.id}>{strategy.name}</Option>
                                  ))}
                                </PermissionedSelect>
                              </div>
                            )}
                            <div style={{width: '40px'}} onClick={(e) => {handleVisibleClick(item, item.visible)}}>
                               <span style={{cursor: `${item.is_key_metric ? 'no-drop': hasEditMetricsPermission ? 'pointer' : 'not-allowed'}`}}>
                                {item.visible ? 
                                    <FaEye style={{opacity: `${item.is_key_metric ? '50%': '100%'}`}} /> : <FaEyeSlash />}
                                </span>
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </div>
                );
              }}
            </Droppable>
          </DragDropContext>
        </div>
      </div>
    </>
  );
};
